import React, { useMemo, useState } from 'react';
import { FileClockIcon, LockIcon, SearchIcon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Tabs } from '../../components/ui/Tabs';
import { Field, Input } from '../../components/ui/Field';
import { EmptyState } from '../../components/ui/EmptyState';
import { PermissionGate } from '../../components/admin/PermissionGate';
import { useGovernance } from '../../contexts/GovernanceContext';
const FILTERS = ['Everything', 'Refused attempts', 'Financial', 'Governance'] as const;
function stamp(value: string): string {
  return new Date(value).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

/**
 * Append-only. There is no delete or edit control on this page for any role,
 * because no administrative role has that authority — including the global
 * super administrator.
 */
export function AuditLog() {
  const {
    audit
  } = useGovernance();
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>('Everything');
  const [query, setQuery] = useState('');
  const records = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return audit.filter(record => {
      if (filter === 'Refused attempts' && record.result !== 'denied') return false;
      if (filter === 'Financial' && !/ledger|refund|payout|transaction|commission|gift/i.test(`${record.action} ${record.resource}`)) return false;
      if (filter === 'Governance' && !/role|invitation|access|configuration|capability/i.test(`${record.action} ${record.resource}`)) return false;
      if (!needle) return true;
      return [record.actor, record.action, record.resource, record.resourceId, record.scope].join(' ').toLowerCase().includes(needle);
    });
  }, [audit, filter, query]);
  return <div className="space-y-6" data-id="element-6264" data-mp="stepslec40zr">
      <PageHeader eyebrow="Administration" title="Audit log" description="Who did what, where, why, and what the value was before and after. Refusals are recorded exactly like successes." actions={<Badge tone="warn" icon={<LockIcon className="h-4 w-4" data-id="element-6267" data-mp="nu1s15lxd9ie" />} data-id="element-6266" data-mp="nsqrdens0hvi">
            Append-only — no role can alter a record
          </Badge>} data-id="element-6265" data-mp="xqocvue940kf" />

      <PermissionGate permission="audit.view" label="Reading the audit log" data-id="element-6268" data-mp="1ggd2d7zc2ose">
        <div className="space-y-5" data-id="element-6269" data-mp="1gih3ji5ul9oz">
          <Panel title="Find a record" data-id="element-6270" data-mp="12q19v7yu6o0q">
            <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)]" data-id="element-6271" data-mp="12ozmc40mw924">
              <Field label="Search" htmlFor="audit-search" data-id="element-6272" data-mp="16prx51p5mm0i">
                <div className="relative" data-id="element-6273" data-mp="1ugfeseovgq09">
                  <SearchIcon aria-hidden className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-ink-subtle" data-id="element-6274" data-mp="1ufm8fetts2in" />
                  <Input id="audit-search" value={query} onChange={event => setQuery(event.target.value)} placeholder="Actor, action, resource or reference" className="pl-11" data-id="element-6275" data-mp="3jw8a06jolbe6" />
                </div>
              </Field>
            </div>
            <div className="mt-4" data-id="element-6276" data-mp="30yf897ksn7id">
              <Tabs tabs={FILTERS} value={filter} onChange={setFilter} label="Audit filters" data-id="element-6277" data-mp="30zqede1porcn" />
            </div>
          </Panel>

          <Panel title={`${records.length} record${records.length === 1 ? '' : 's'}`} bodyClassName="p-0" data-id="element-6278" data-mp="2w1f4527i8etw">
            {records.length === 0 ? <div className="px-5 py-8" data-id="element-6279" data-mp="35w6zs0hj0rm1">
                <EmptyState icon={<FileClockIcon className="h-5 w-5" data-id="element-6281" data-mp="q5krxdgd5m3y" />} title="No matching records" description="Nothing in the log matches that filter or search." data-id="element-6280" data-mp="q6m27fpk24km" />
              </div> : <ul className="divide-y divide-line" data-id="element-6282" data-mp="2qxwdsee2wa7y">
                {records.map(record => <li key={record.id} className="px-5 py-4 sm:px-6" data-id="element-6283" data-mp="2vx0kmydj5vv4">
                    <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2" data-id="element-6284" data-mp="2zvq85jsa8tsh">
                      <div className="min-w-0" data-id="element-6285" data-mp="34wdwtyt1gyjj">
                        <p className="text-body font-semibold text-ink" data-id="element-6286" data-mp="39up6o9jc391r">{record.action}</p>
                        <p className="mt-0.5 text-body-sm text-ink-muted" data-id="element-6287" data-mp="39suv3ltyctac">
                          {record.resource} · <span className="cz-num" data-id="element-6288" data-mp="3erfl6sfhpwl2">{record.resourceId}</span>
                        </p>
                        <p className="cz-num mt-0.5 text-body-sm text-ink" data-id="element-6289" data-mp="3jmuxvh1rhmfw">
                          {record.actor}{' '}
                          <span className="text-ink-muted" data-id="element-6290" data-mp="3slf6fpdlyfqe">— {record.actorRole}</span>
                        </p>
                        <p className="mt-0.5 text-caption text-ink-subtle" data-id="element-6291" data-mp="1e2sdj80uiep">
                          {record.scope} · {stamp(record.at)}
                        </p>
                      </div>
                      <Badge tone={record.result === 'succeeded' ? 'ok' : record.result === 'denied' ? 'neutral' : 'danger'} data-id="element-6292" data-mp="6j6jtnezekdt">
                        {record.result === 'denied' ? 'Refused' : record.result}
                      </Badge>
                    </div>

                    {(record.before || record.after) && <dl className="mt-3 grid gap-3 rounded-md border border-line bg-canvas px-3 py-2.5 sm:grid-cols-2" data-id="element-6293" data-mp="khxvz4aa9vzb">
                        <div data-id="element-6294" data-mp="gg2vg206fmml">
                          <dt className="text-body-sm text-ink-muted" data-id="element-6295" data-mp="1d0bkuedn67m9">Before</dt>
                          <dd className="text-body text-ink" data-id="element-6296" data-mp="1893btjg84o9m">{record.before ?? 'Not applicable'}</dd>
                        </div>
                        <div data-id="element-6297" data-mp="32gnlwokttqxu">
                          <dt className="text-body-sm text-ink-muted" data-id="element-6298" data-mp="32hfh3yaihmnl">After</dt>
                          <dd className="text-body text-ink" data-id="element-6299" data-mp="37eyxp4uombip">{record.after ?? 'Not applicable'}</dd>
                        </div>
                      </dl>}

                    <p className="mt-2 max-w-[76ch] text-body-sm text-ink" data-id="element-6300" data-mp="2xpp059h2k0n7">
                      <span className="text-ink-muted" data-id="element-6301" data-mp="2jqfnukvzhqoi">Reason: </span>
                      {record.reason}
                    </p>
                  </li>)}
              </ul>}
          </Panel>
        </div>
      </PermissionGate>
    </div>;
}